import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { ApiService, FormService } from '@app-core/services';
import { OverlayRef } from '@app-core/services/overlay';
import { CheckoutFormValues, LienholderResponse } from '@app-model/checkout';
import { Forms } from '@app-model/form';
import { GetPostalCodeResponse } from '@app-model/response';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { untilDestroyed } from '@app-shared/utils';
import * as CartActions from '@app-store/cart/cart.actions';
import * as fromRoot from '@app-store/reducers';
import { map, withLatestFrom } from 'rxjs/operators';

interface ILienholderForm {
  lienholderName: FormControl<string>;
  lienholderAddress1: FormControl<string>;
  lienholderAddress2: FormControl<string>;
  lienholderCountryCode: FormControl<string>;
  lienholderPostalCode: FormControl<string>;
  lienholderCity: FormControl<string>;
  lienholderStateProvince: FormControl<string>;
}

@Component({
  templateUrl: './add-lienholder-popup.component.html',
  styleUrls: ['./add-lienholder-popup.component.scss'],
  imports: [IconComponent, InputControlComponent, ReactiveFormsModule, ButtonComponent],
})
export class AddLienholderPopupComponent {
  private apiService = inject(ApiService);
  private formService = inject(FormService);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private ref = inject<OverlayRef<AddLienholderPopupComponent>>(OverlayRef);

  lienholderForm: FormGroup<ILienholderForm>;
  autoFill: boolean = false;

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    this.store$
      .select((store) => store.cartState.checkoutForms)
      .pipe(
        withLatestFrom(this.store$.select((state) => state.cartState.checkoutFormValues)),
        map(([formValues, checkoutValues]: [Forms, CheckoutFormValues]) => {
          const formGroup: FormGroup<ILienholderForm> = this.formService.setForm(formValues.addlienholderForm);
          formGroup.patchValue(checkoutValues.customer);

          return Object.assign({ formValues, formGroup });
        }),
        takeUntilDestroyed()
      )
      .subscribe((response) => (this.lienholderForm = response.formGroup));
  }

  close(): void {
    this.ref.close();
  }

  getPostalCode(): void {
    if (this.lienholderForm.get('lienholderPostalCode')?.valid) {
      const postalcode = this.lienholderForm.get('lienholderPostalCode').value;

      this.apiService
        .getPostal({ postalcode })
        .pipe(this.#untilDestroyed())
        .subscribe((postal: GetPostalCodeResponse) => {
          if (postal.status) {
            this.autoFill = true;
            this.lienholderForm.get('lienholderCity').markAsPristine();
            this.lienholderForm.get('lienholderStateProvince').markAsPristine();
            this.lienholderForm.get('lienholderCountryCode').markAsPristine();
            this.lienholderForm.get('lienholderCity').setValue(postal.city);
            this.lienholderForm.get('lienholderStateProvince').setValue(postal.stateAbbr);
            this.lienholderForm.get('lienholderCountryCode').setValue(postal.country);
          } else {
            this.autoFill = false;
            this.lienholderForm?.get('lienholderCity')?.setValue('');
            this.lienholderForm?.get('lienholderStateProvince')?.setValue('');
            this.lienholderForm?.get('lienholderCountryCode')?.setValue('');
          }
        });
    }
  }

  addLienholder(): void {
    if (this.lienholderForm.invalid) {
      return;
    }
    this.apiService
      .addLienholder(this.lienholderForm.value)
      .pipe(this.#untilDestroyed())
      .subscribe((data: { newLienholder: LienholderResponse }) => {
        this.store$.dispatch(new CartActions.UpdateLienholderWithNewlyAddedAction(data.newLienholder));
        this.ref.close();
      });
  }
}
